import { inject, Injectable } from '@angular/core';
import {
  ClaimSessionInput,
  ClaimSessionMutation,
  FindSessionByIdQuery,
  LoadSimplifiedSessionQuery,
  DashboardListSessionPartsFragment,
  RetakePhotosInput,
  RetakePhotosMutation,
  SessionsForDashboardWithLiveIdQuery,
  SessionsForDashboardWithOfflineIdQuery,
  SessionsForDashboardWithPendingRetakeRequestsQuery,
  SessionsForDashboardWithRetakenRequestsQuery,
  SessionsFromEncryptedTokenQuery,
  EncryptorNameEnum
} from '../queries/generated';
import { from, map, Subject, switchMap, startWith } from 'rxjs';
import { finalMap } from '../rx/final-map';

/// Short version of the session, with details for the dashboard.
export type DashboardSessionDetails = DashboardListSessionPartsFragment;

/// Allowed types of sessions to fetch from the dashboard.
export type SessionListType = 'onlineId' | 'offlineId' | 'retaken' | 'pendingRetake';

@Injectable({
  providedIn: 'root'
})
export class SessionsService {
  /// Subject to trigger session list reloads
  private readonly sessionListReloadTrigger$ = new Subject<void>();

  /// Queries (reading data).
  private readonly withLiveIdQuery = inject(SessionsForDashboardWithLiveIdQuery);
  private readonly withOfflineIdQuery = inject(SessionsForDashboardWithOfflineIdQuery);
  private readonly withRetakenRequestsQuery = inject(SessionsForDashboardWithRetakenRequestsQuery);
  private readonly pendingRetakeRequestsQuery = inject(SessionsForDashboardWithPendingRetakeRequestsQuery);
  private readonly loadSimplifiedSessionQuery = inject(LoadSimplifiedSessionQuery);
  private readonly sessionsFromEncryptedToken = inject(SessionsFromEncryptedTokenQuery);
  private readonly findSessionByIdQuery = inject(FindSessionByIdQuery);

  /// Mutations (writing data).
  private readonly claimSessionMutation = inject(ClaimSessionMutation);
  private readonly retakePhotosMutation = inject(RetakePhotosMutation);

  /// Let's fetch only the first 25 sessions of each type for the dashboard.
  readonly dashboardSessionsPerPage = 25;

  find(id: string, ignoreCache = false) {
    const fetchPolicy = ignoreCache ? 'network-only' : 'cache-first';

    return this.findSessionByIdQuery
      .watch({ id }, { fetchPolicy })
      .valueChanges.pipe(map(({ data }) => data.sessions.findById));
  }

  /**
   * Retuns a collection of sessions from an encrypted token.
   */
  fromEncryptedToken(token: string, encryptor: EncryptorNameEnum) {
    return this.sessionsFromEncryptedToken
      .watch({ token, encryptor })
      .valueChanges.pipe(map(({ data }) => data.public.sessionsFromEncryptedToken));
  }

  liveIdSessions() {
    return this.sessionListReloadTrigger$.pipe(
      startWith([]),
      switchMap(() => this.withLiveIdQuery.watch({ first: this.dashboardSessionsPerPage }).valueChanges),
      map((result) => result.data.sessions.forDashboardWithLiveId.nodes)
    );
  }

  offlineIdSessions() {
    return this.sessionListReloadTrigger$.pipe(
      startWith([]),
      switchMap(() => this.withOfflineIdQuery.watch({ first: this.dashboardSessionsPerPage }).valueChanges),
      map((result) => result.data.sessions.forDashboardWithOfflineId.nodes)
    );
  }

  retakenSessions() {
    return this.sessionListReloadTrigger$.pipe(
      startWith([]),
      switchMap(() => this.withRetakenRequestsQuery.watch({ first: this.dashboardSessionsPerPage }).valueChanges),
      map((result) => result.data.sessions.forDashboardWithRetakenRequests.nodes)
    );
  }

  pendingRetakeSessions() {
    return this.sessionListReloadTrigger$.pipe(
      startWith([]),
      switchMap(() => this.pendingRetakeRequestsQuery.watch({ first: this.dashboardSessionsPerPage }).valueChanges),
      map((result) => result.data.sessions.forDashboardWithPendingRetakeRequests.nodes)
    );
  }

  refetchOnlineIdSessions() {
    return from(this.withLiveIdQuery.watch({ first: this.dashboardSessionsPerPage }).refetch()).pipe(
      map((result) => result.data.sessions.forDashboardWithLiveId.nodes)
    );
  }

  /**
   * We don't want the whole session object, with all the fields and relationships,
   * because we are using it when a new online ID session starts and it should pop up
   * in the dashboard.
   */
  loadSimplifiedSession(id: string) {
    return this.loadSimplifiedSessionQuery
      .watch({ id })
      .valueChanges.pipe(map((result) => result.data.sessions.findById));
  }

  /**
   * Performs the mutation to claim a session for the current user.
   */
  claimSession(input: ClaimSessionInput) {
    return this.claimSessionMutation
      .mutate(
        { input },
        {
          refetchQueries: [
            this.withLiveIdQuery.document,
            this.withOfflineIdQuery.document,
            this.withRetakenRequestsQuery.document,
            this.pendingRetakeRequestsQuery.document
          ]
        }
      )
      .pipe(
        map(({ data }) => data.claimSession),
        finalMap('claimedSession')
      );
  }

  retakePhotos(input: RetakePhotosInput) {
    return this.retakePhotosMutation.mutate({ input }).pipe(
      map(({ data }) => data.retakePhotos),
      finalMap('retakenSession')
    );
  }

  // Just let the observables know that they should reload.
  emitReloadTrigger() {
    this.sessionListReloadTrigger$.next();
  }
}
